// ============================================================
// services/firebaseServices.js
// ============================================================
// Frontend service layer — wraps all Firestore/Auth/Storage calls
// Import these functions in your React components

import {
  collection, doc, getDoc, getDocs, addDoc, setDoc, updateDoc, deleteDoc,
  query, where, orderBy, limit, startAfter, onSnapshot,
  serverTimestamp, increment, arrayUnion, arrayRemove,
  Timestamp,
} from "firebase/firestore";
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signOut, updateProfile, onAuthStateChanged,
  GoogleAuthProvider, signInWithPopup, sendPasswordResetEmail,
} from "firebase/auth";
import {
  ref, uploadBytes, getDownloadURL, deleteObject,
} from "firebase/storage";
import { httpsCallable } from "firebase/functions";

import { db, auth, storage, functions } from "../firebase.config";

// ============================================================
// AUTH SERVICE
// ============================================================
export const AuthService = {
  // Sign up with email & password
  async signUp(email, password, displayName, phone = "") {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName });
    // Profile creation handled in AuthContext.jsx
    return cred.user;
  },

  // Sign in with email & password
  async signIn(email, password) {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    // Update last login (safe — profile exists by now)
    try {
      await updateDoc(doc(db, "users", cred.user.uid), {
        lastLoginAt: serverTimestamp(),
      });
    } catch (e) {
      console.warn("Could not update lastLogin:", e.message);
    }
    return cred.user;
  },

  // Google sign in
  async signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    const cred = await signInWithPopup(auth, provider);
    return cred.user;
  },

  // Sign out
  async signOut() {
    await signOut(auth);
  },

  // Password reset
  async resetPassword(email) {
    await sendPasswordResetEmail(auth, email);
  },

  // Listen to auth state
  onAuthChange(callback) {
    return onAuthStateChanged(auth, callback);
  },

  // Get current user
  getCurrentUser() {
    return auth.currentUser;
  },
};

// ============================================================
// USER SERVICE
// ============================================================
export const UserService = {
  // Create user profile on signup
  async createProfile(uid, { email, displayName, phone }) {
    await setDoc(doc(db, "users", uid), {
      uid,
      email: email || "",
      displayName: displayName || "",
      phone: phone || "",
      photoURL: null,
      role: "customer",
      address: "",
      dateOfBirth: null,
      preferences: {
        dietaryRestrictions: [],
        favouriteProducts: [],
        communicationPrefs: { email: true, whatsapp: true, push: true },
      },
      loyaltyPoints: 0,
      loyaltyTier: "bronze",
      totalOrders: 0,
      totalSpent: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
      isActive: true,
    });
  },

  // Get user profile
  async getProfile(uid) {
    const snap = await getDoc(doc(db, "users", uid));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  // Update user profile
  async updateProfile(uid, data) {
    await updateDoc(doc(db, "users", uid), {
      ...data,
      updatedAt: serverTimestamp(),
    });
  },

  // Get all customers (admin)
  async getAllCustomers() {
    const q = query(
      collection(db, "users"),
      orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  // Update user role (admin)
  async updateRole(uid, role) {
    await updateDoc(doc(db, "users", uid), {
      role,
      updatedAt: serverTimestamp(),
    });
  },

  // Upload profile picture
  async uploadProfilePicture(uid, file) {
    const storageRef = ref(storage, `users/${uid}/profile/${file.name}`);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    await updateDoc(doc(db, "users", uid), { photoURL: url });
    return url;
  },
};

// ============================================================
// PRODUCT SERVICE
// ============================================================
export const ProductService = {
  // Get all available products (public)
  async getAll() {
    const q = query(
      collection(db, "products"),
      where("available", "==", true),
      orderBy("sortOrder", "asc")
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  // Get all products including hidden (admin)
  async getAllAdmin() {
    const q = query(collection(db, "products"), orderBy("sortOrder", "asc"));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  // Get products by category
  async getByCategory(category) {
    const q = query(
      collection(db, "products"),
      where("available", "==", true),
      where("category", "==", category),
      orderBy("sortOrder", "asc")
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  // Get featured products
  async getFeatured() {
    const q = query(
      collection(db, "products"),
      where("available", "==", true),
      where("featured", "==", true),
      orderBy("sortOrder", "asc")
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  // Get single product
  async getById(productId) {
    const snap = await getDoc(doc(db, "products", productId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  // Create product (admin)
  async create(productData) {
    const slug = productData.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    const docRef = await addDoc(collection(db, "products"), {
      ...productData,
      slug,
      averageRating: 0,
      totalReviews: 0,
      totalOrders: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: auth.currentUser?.uid || "system",
    });
    return docRef.id;
  },

  // Update product (admin)
  async update(productId, data) {
    await updateDoc(doc(db, "products", productId), {
      ...data,
      updatedAt: serverTimestamp(),
    });
  },

  // Delete product (admin)
  async delete(productId) {
    // Delete images from Storage
    try {
      const product = await getDoc(doc(db, "products", productId));
      const data = product.data();
      if (data?.thumbnailURL) {
        const imgRef = ref(storage, data.thumbnailURL);
        await deleteObject(imgRef).catch(() => {});
      }
      if (data?.images?.length) {
        for (const url of data.images) {
          const imgRef = ref(storage, url);
          await deleteObject(imgRef).catch(() => {});
        }
      }
    } catch (e) {
      console.warn("Could not delete product images:", e.message);
    }
    await deleteDoc(doc(db, "products", productId));
  },

  // Upload product image
  async uploadImage(productId, file) {
    const storageRef = ref(storage, `products/${productId}/${file.name}`);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    // Add to product's images array
    await updateDoc(doc(db, "products", productId), {
      images: arrayUnion(url),
      thumbnailURL: url, // Set as thumbnail too
      updatedAt: serverTimestamp(),
    });
    return url;
  },

  // Listen to products in real-time
  onProductsChange(callback) {
    const q = query(
      collection(db, "products"),
      where("available", "==", true),
      orderBy("sortOrder", "asc")
    );
    return onSnapshot(q, (snap) => {
      callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (error) => {
      console.warn("Products listener error:", error.message);
      callback([]);
    });
  },
};

// ============================================================
// BOOKING SERVICE
// ============================================================
export const BookingService = {
  // Create a new booking
  async create(bookingData) {
    const docRef = await addDoc(collection(db, "bookings"), {
      ...bookingData,
      customerId: auth.currentUser?.uid || "guest",
      status: "pending",
      paymentStatus: "unpaid",
      statusHistory: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  },

  // Get customer's bookings
  async getMyBookings() {
    const uid = auth.currentUser?.uid;
    if (!uid) return [];
    const q = query(
      collection(db, "bookings"),
      where("customerId", "==", uid),
      orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  // Get all bookings (admin)
  async getAllAdmin(statusFilter = null) {
    let q;
    if (statusFilter) {
      q = query(
        collection(db, "bookings"),
        where("status", "==", statusFilter),
        orderBy("createdAt", "desc")
      );
    } else {
      q = query(collection(db, "bookings"), orderBy("createdAt", "desc"));
    }
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  // Update booking status (admin/staff)
  async updateStatus(bookingId, newStatus, note = "") {
    await updateDoc(doc(db, "bookings", bookingId), {
      status: newStatus,
      statusHistory: arrayUnion({
        status: newStatus,
        changedBy: auth.currentUser?.uid || "system",
        changedAt: Timestamp.now(),
        note,
      }),
      updatedAt: serverTimestamp(),
      ...(newStatus === "confirmed" ? { confirmedAt: serverTimestamp() } : {}),
      ...(newStatus === "completed" ? { completedAt: serverTimestamp() } : {}),
      ...(newStatus === "cancelled"
        ? { cancelledAt: serverTimestamp(), cancellationReason: note }
        : {}),
    });
  },

  // Cancel my booking (customer)
  async cancelMyBooking(bookingId, reason = "") {
    const bookingDoc = await getDoc(doc(db, "bookings", bookingId));
    const booking = bookingDoc.data();
    if (booking.customerId !== auth.currentUser?.uid) throw new Error("Not your booking");
    if (booking.status !== "pending") throw new Error("Can only cancel pending bookings");
    await this.updateStatus(bookingId, "cancelled", reason);
  },

  // Check slot availability (calls Cloud Function)
  async checkAvailability(date, time) {
    const checkFn = httpsCallable(functions, "checkSlotAvailability");
    const result = await checkFn({ date, time });
    return result.data;
  },

  // Listen to bookings in real-time (admin)
  onBookingsChange(callback) {
    const q = query(collection(db, "bookings"), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => {
      callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (error) => {
      console.warn("Bookings listener error:", error.message);
      callback([]);
    });
  },
};

// ============================================================
// REVIEW SERVICE
// ============================================================
export const ReviewService = {
  // Get reviews for a product
  async getByProduct(productId) {
    const q = query(
      collection(db, "reviews"),
      where("productId", "==", productId),
      where("isApproved", "==", true),
      orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  // Get all reviews (admin)
  async getAllAdmin() {
    const q = query(collection(db, "reviews"), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  // Create a review
  async create(reviewData) {
    const user = auth.currentUser;
    if (!user) throw new Error("Must be signed in");
    const docRef = await addDoc(collection(db, "reviews"), {
      ...reviewData,
      customerId: user.uid,
      customerName: user.displayName || "Anonymous",
      customerPhotoURL: user.photoURL || null,
      isApproved: true,
      isFlagged: false,
      adminReply: null,
      adminReplyAt: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  },

  // Admin reply to review
  async addAdminReply(reviewId, reply) {
    await updateDoc(doc(db, "reviews", reviewId), {
      adminReply: reply,
      adminReplyAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  },

  // Toggle review visibility (admin)
  async toggleApproval(reviewId, isApproved) {
    await updateDoc(doc(db, "reviews", reviewId), {
      isApproved,
      updatedAt: serverTimestamp(),
    });
  },
};

// ============================================================
// LOYALTY SERVICE
// ============================================================
export const LoyaltyService = {
  // Get loyalty config
  async getConfig() {
    const snap = await getDoc(doc(db, "loyalty", "config"));
    return snap.exists() ? snap.data() : null;
  },

  // Update loyalty config (admin)
  async updateConfig(configData) {
    await updateDoc(doc(db, "loyalty", "config"), {
      ...configData,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid || "system",
    });
  },

  // Get my loyalty transactions
  async getMyTransactions() {
    const uid = auth.currentUser?.uid;
    if (!uid) return [];
    const q = query(
      collection(db, "loyalty", "transactions", "all"),
      where("customerId", "==", uid),
      orderBy("createdAt", "desc"),
      limit(50)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },
};

// ============================================================
// PROMOTION SERVICE
// ============================================================
export const PromotionService = {
  // Get active promotions (customer)
  async getActive() {
    const now = Timestamp.now();
    const q = query(
      collection(db, "promotions"),
      where("isActive", "==", true),
      where("endDate", ">=", now),
      orderBy("endDate", "asc")
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  // Get all promotions (admin)
  async getAllAdmin() {
    const q = query(collection(db, "promotions"), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  // Create promotion (admin)
  async create(promoData) {
    const docRef = await addDoc(collection(db, "promotions"), {
      ...promoData,
      currentUses: 0,
      createdAt: serverTimestamp(),
      createdBy: auth.currentUser?.uid || "system",
    });
    return docRef.id;
  },

  // Update promotion (admin)
  async update(promoId, data) {
    await updateDoc(doc(db, "promotions", promoId), data);
  },

  // Delete promotion (admin)
  async delete(promoId) {
    await deleteDoc(doc(db, "promotions", promoId));
  },

  // Validate promo code
  async validateCode(code) {
    const q = query(
      collection(db, "promotions"),
      where("code", "==", code.toUpperCase()),
      where("isActive", "==", true)
    );
    const snap = await getDocs(q);
    if (snap.empty) return { valid: false, reason: "Invalid promo code." };
    const promo = { id: snap.docs[0].id, ...snap.docs[0].data() };
    const now = new Date();
    if (promo.endDate.toDate() < now)
      return { valid: false, reason: "This promo has expired." };
    if (promo.maxUses && promo.currentUses >= promo.maxUses)
      return { valid: false, reason: "This promo has been fully redeemed." };
    return { valid: true, promotion: promo };
  },
};

// ============================================================
// NOTIFICATION SERVICE
// ============================================================
export const NotificationService = {
  // Get my notifications
  async getMyNotifications() {
    const uid = auth.currentUser?.uid;
    if (!uid) return [];
    const q = query(
      collection(db, "notifications"),
      where("recipientId", "==", uid),
      orderBy("createdAt", "desc"),
      limit(50)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  // Mark as read
  async markAsRead(notifId) {
    await updateDoc(doc(db, "notifications", notifId), {
      isRead: true,
      readAt: serverTimestamp(),
    });
  },

  // Mark all as read
  async markAllAsRead() {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const q = query(
      collection(db, "notifications"),
      where("recipientId", "==", uid),
      where("isRead", "==", false)
    );
    const snap = await getDocs(q);
    const promises = snap.docs.map((d) =>
      updateDoc(d.ref, { isRead: true, readAt: serverTimestamp() })
    );
    await Promise.all(promises);
  },

  // Get unread count
  async getUnreadCount() {
    const uid = auth.currentUser?.uid;
    if (!uid) return 0;
    const q = query(
      collection(db, "notifications"),
      where("recipientId", "==", uid),
      where("isRead", "==", false)
    );
    const snap = await getDocs(q);
    return snap.size;
  },

  // Listen to notifications in real-time
  onNotificationsChange(callback) {
    const uid = auth.currentUser?.uid;
    if (!uid) return () => {};
    const q = query(
      collection(db, "notifications"),
      where("recipientId", "==", uid),
      orderBy("createdAt", "desc"),
      limit(20)
    );
    return onSnapshot(q, (snap) => {
      callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (error) => {
      console.warn("Notifications listener error:", error.message);
      callback([]);
    });
  },
};

// ============================================================
// BUSINESS SETTINGS SERVICE
// ============================================================
export const BusinessSettingsService = {
  // Get settings (public)
  async get() {
    const snap = await getDoc(doc(db, "businessSettings", "config"));
    return snap.exists() ? snap.data() : null;
  },

  // Update settings (admin)
  async update(data) {
    await updateDoc(doc(db, "businessSettings", "config"), {
      ...data,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid || "system",
    });
  },

  // Add blocked date
  async addBlockedDate(date, reason) {
    await updateDoc(doc(db, "businessSettings", "config"), {
      blockedDates: arrayUnion({ date, reason }),
      updatedAt: serverTimestamp(),
    });
  },

  // Remove blocked date
  async removeBlockedDate(dateObj) {
    await updateDoc(doc(db, "businessSettings", "config"), {
      blockedDates: arrayRemove(dateObj),
      updatedAt: serverTimestamp(),
    });
  },
};

// ============================================================
// CAMPAIGN SERVICE
// ============================================================
export const CampaignService = {
  // Get all campaigns (admin)
  async getAll() {
    const q = query(collection(db, "campaigns"), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  // Create campaign
  async create(data) {
    const docRef = await addDoc(collection(db, "campaigns"), {
      ...data,
      status: "draft",
      totalSent: 0,
      totalOpened: 0,
      totalClicked: 0,
      createdAt: serverTimestamp(),
      createdBy: auth.currentUser?.uid || "system",
    });
    return docRef.id;
  },

  // Send campaign (calls Cloud Function)
  async send(campaignId) {
    const sendFn = httpsCallable(functions, "sendCampaign");
    const result = await sendFn({ campaignId });
    return result.data;
  },

  // Delete campaign
  async delete(campaignId) {
    // Delete image from Storage
    try {
      const campaign = await getDoc(doc(db, "campaigns", campaignId));
      const data = campaign.data();
      if (data?.imageURL) {
        const imgRef = ref(storage, data.imageURL);
        await deleteObject(imgRef).catch(() => {});
      }
    } catch (e) {
      console.warn("Could not delete campaign image:", e.message);
    }
    await deleteDoc(doc(db, "campaigns", campaignId));
  },
};